// Generational suffixes that may trail a patient name, mirroring
// DuplicatePatientDetector::GENERATIONAL_SUFFIXES on the backend.
const GENERATIONAL_SUFFIXES = ['jr', 'sr', 'ii', 'iii', 'iv'];

// True when a token is a generational suffix, ignoring case and punctuation
// (e.g. "Jr." / "III") — the backend strips punctuation before this check, so
// the search term must too or it would return "Jr." as the surname.
const isSuffixToken = (token) => GENERATIONAL_SUFFIXES.includes(
  token.toLowerCase().replace(/[^a-z]/g, ''),
);

// Derives the patient's last name from the (unstructured) patient_name string
// the inbox payload exposes, so the duplicate-alert search can narrow a DOB-only
// match down with `contains=<last name>`. The backend duplicate detector
// normalizes names (it keeps first + last, drops middle initials and suffixes),
// so a DOB-only link can surface many unrelated same-DOB patients; adding the
// last name removes ~90-99% of that noise while still matching every record
// that triggered the alert (real duplicates always share the last name).
//
// Mirrors the backend normalizer's last-token logic: flips "Last, First" to
// "First Last", then takes the final token after dropping trailing suffixes.
//
// The returned surname feeds the inbox `contains` filter, which is a literal
// `LIKE %term%` against the raw stored name. The backend duplicate detector,
// however, equates punctuation the raw text keeps: it folds hyphens to spaces
// and deletes apostrophes, so "Smith-Jones" / "Smith Jones" and "O'Brien" /
// "OBrien" are each one patient. To stay a substring of every spelling a
// sibling might be stored as, we return the final maximal alphanumeric run of
// the surname ("Jones", "Brien") rather than the punctuated token.
export const patientLastNameForSearch = (patientName) => {
  const raw = (patientName || '').trim();
  if (!raw) return '';

  let normalized = raw;
  if (raw.includes(',')) {
    const [last, first] = raw.split(',').map((part) => part.trim());
    if (first && last) {
      normalized = `${first} ${last}`;
    }
  }

  // Split on whitespace and hyphens so a hyphenated surname yields its final
  // bare token, matching how the backend folds hyphens to spaces.
  const tokens = normalized.split(/[\s-]+/).filter(Boolean);
  while (tokens.length > 1 && isSuffixToken(tokens[tokens.length - 1])) {
    tokens.pop();
  }

  if (!tokens.length) return '';

  // Return the last alphanumeric run so interior punctuation the backend
  // ignores (e.g. the apostrophe in "O'Brien") can't break the literal LIKE.
  const runs = tokens[tokens.length - 1].match(/[\p{L}\p{N}]+/gu);
  return runs ? runs[runs.length - 1] : '';
};

export default patientLastNameForSearch;
